import { Grid, InputLabel, TextField, Typography } from "@mui/material";
import { selectHardwareDeliveryCountries } from "features/customer";
import { selectHardwarePurchaseType } from "features/hardware/hardwarePage";
import { useAppSelector } from "hooks/hooks";
import { UseFormReturn } from "react-hook-form";
import { DELIVERY_TIME } from "utilities/constants/constants";
import {
	DeliveryOption,
	HardwareDeliveryCountryOption,
	HardwarePurchaseType,
} from "utilities/constants/enums";
import { userFormValidation } from "utilities/userFormValidation";
import styles from "./ShippingDetails.module.scss";

interface Props {
	formProps: UseFormReturn;
	xs?: number;
	required?: boolean;
	isOnboarding?: boolean;
	disabled?: boolean;
	countryDisabled?: boolean;
}

const ShippingDetails = ({
	formProps: {
		register,
		watch,
		formState: { errors },
	},
	xs = 4,
	required = true,
	isOnboarding = false,
	disabled = false,
	countryDisabled = false,
}: Props) => {
	const hardwareDeliveryCountries = useAppSelector(selectHardwareDeliveryCountries);
	const hardwarePurchaseType = useAppSelector(selectHardwarePurchaseType);

	return (
		<Grid
			container
			item
			lg={xs}
			md={12}
			className={styles.shippingDetailsContainer}
			justifyContent="flex-start"
		>
			<Grid item xs={12}>
				<InputLabel>
					<Typography variant="body1" display="inline">
						Street address
					</Typography>
					{isOnboarding && (
						<>
							{watch("deliveryOption") === DeliveryOption.B2C ? (
								<Typography
									variant="caption"
									color="text.secondary"
									display="inline"
								>
									{" "}
									(Received at <b>home address</b> between {DELIVERY_TIME.HOME})
								</Typography>
							) : (
								<Typography
									variant="caption"
									color="text.secondary"
									display="inline"
								>
									{" "}
									(Received at <b>office address</b> between{" "}
									{DELIVERY_TIME.OFFICE})
								</Typography>
							)}{" "}
						</>
					)}
				</InputLabel>
				<TextField
					fullWidth
					size="small"
					InputProps={{
						classes: {
							root: disabled ? styles.disabled : "",
							input: styles.formTextInput,
						},
					}}
					error={!!errors.streetAddress}
					helperText={userFormValidation.streetAddress.getHelperText({
						streetAddress: errors.streetAddress,
					})}
					{...register(
						"streetAddress",
						userFormValidation.streetAddress.getValidationProps(required),
					)}
				/>
			</Grid>
			<Grid item xs={2.75} mr={3}>
				<InputLabel>
					<Typography variant="body1">Postal code</Typography>
				</InputLabel>
				<TextField
					fullWidth
					size="small"
					error={!!errors.postalCode}
					InputProps={{
						classes: {
							root: disabled ? styles.disabled : "",
							input: styles.formTextInput,
						},
					}}
					helperText={userFormValidation.postalCode.getHelperText({
						postalCode: errors.postalCode,
					})}
					{...register(
						"postalCode",
						userFormValidation.postalCode.getValidationProps(required),
					)}
				/>
			</Grid>
			<Grid item xs={2.75} mr={3}>
				<InputLabel>
					<Typography variant="body1">City</Typography>
				</InputLabel>
				<TextField
					fullWidth
					size="small"
					InputProps={{
						classes: {
							root: disabled ? styles.disabled : "",
							input: styles.formTextInput,
						},
					}}
					error={!!errors.city}
					helperText={userFormValidation.city.getHelperText({
						city: errors.city,
					})}
					{...register("city", userFormValidation.city.getValidationProps(required))}
				/>
			</Grid>
			<Grid item xs={2.75}>
				<InputLabel>
					<Typography variant="body1">Country</Typography>
				</InputLabel>
				{hardwarePurchaseType === HardwarePurchaseType.Lease ? (
					<TextField
						fullWidth
						size="small"
						InputProps={{
							classes: {
								root: disabled || countryDisabled ? styles.disabled : "",
								input: styles.formTextInput,
							},
						}}
						error={!!errors.shippingCountry}
						helperText={userFormValidation.country.getHelperText({
							country: errors.shippingCountry,
						})}
						{...register(
							"shippingCountry",
							userFormValidation.country.getValidationProps(required),
						)}
					/>
				) : (
					<TextField
						fullWidth
						size="small"
						value={
							hardwareDeliveryCountries === HardwareDeliveryCountryOption.Norway
								? "Norway"
								: hardwareDeliveryCountries === HardwareDeliveryCountryOption.Sweden
									? "Sweden"
									: "Norway"
						}
						disabled
						InputProps={{
							classes: {
								root: styles.disabled,
								input: styles.formTextInput,
							},
						}}
					/>
				)}
			</Grid>
		</Grid>
	);
};

export { ShippingDetails };
